/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { clsx } from 'clsx';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'default' | 'purple';

const variantStyles: Record<BadgeVariant, string> = {
  success: 'bg-status-success-bg text-status-success-fg',
  warning: 'bg-status-warning-bg text-status-warning-fg',
  error: 'bg-status-critical-bg text-status-critical-fg',
  info: 'bg-primary/10 text-primary',
  default: 'bg-status-neutral-bg text-status-neutral-fg',
  purple: 'bg-primary-100 text-primary-700',
};

type BadgeSize = 'sm' | 'md';

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-2 py-0.5',
  md: 'text-xs px-2.5 py-0.5',
};

interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'className' | 'children'> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}

export default function Badge({
  variant = 'default',
  size = 'md',
  children,
  className,
  dot,
  ...rest
}: BadgeProps) {
  // `rest` carries the span attributes callers pass (aria-hidden, title, data-*);
  // without the spread they were silently dropped (Header's aria-hidden badge).
  return (
    <span
      {...rest}
      className={clsx(
        'inline-flex items-center gap-1.5 font-medium rounded-full',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}
